import { useMemo } from 'react';

import { SubscriptionPlanType } from '@repo/subscription/api';
import {
  subscriptionStoreSelector,
  useSubscriptionStore,
} from '@repo/subscription/client';

import {
  planSelectionStoreSelector,
  usePlanSelectionStore,
} from '../../../stores/planSelectionStore';
import { SupportedCurrency } from '../../../types/SubscriptionPrice';
import { getPlanPrice } from '../utils';

export type BillingPrice = {
  id: string;
  quantity: number;
  amount: number;
  currency: SupportedCurrency;
};

/**
 * Calculates the billing prices based on the selected plan, billing interval, quantity, and any applicable discounts.
 */
const useBillingPrices = (): BillingPrice[] | undefined => {
  const plan = usePlanSelectionStore(planSelectionStoreSelector.plan);
  const isYearly = usePlanSelectionStore(planSelectionStoreSelector.yearly);
  const expertAddonPriceId = usePlanSelectionStore(
    planSelectionStoreSelector.expertAddonPriceId
  );
  const quantity = usePlanSelectionStore(planSelectionStoreSelector.quantity);
  const subscriptionPrices = useSubscriptionStore(
    subscriptionStoreSelector.subscriptionPrices
  );
  const country = useSubscriptionStore(
    subscriptionStoreSelector.requestCountry
  );

  const pricesToChange = useMemo(() => {
    if (!subscriptionPrices.length) {
      return undefined;
    }

    const price = getPlanPrice({
      plan,
      isYearly,
      subscriptionPrices,
      country,
    });
    const planPriceId = price.id;

    if (!planPriceId) {
      return undefined;
    }

    const result = [
      {
        id: planPriceId,
        quantity: quantity ?? 1,
        amount: isYearly ? price.amount * 12 : price.amount,
        currency: price.currency,
      },
    ];

    if (plan === SubscriptionPlanType.EXPERT && expertAddonPriceId) {
      // TODO: Addon is no longer supported, cleanup later. https://app.clickup.com/t/86bz8c54t
      // result.push({ price: expertAddonPriceId, quantity: 1 });
    }

    return result;
  }, [
    plan,
    isYearly,
    subscriptionPrices,
    country,
    quantity,
    expertAddonPriceId,
  ]);

  return pricesToChange;
};

export default useBillingPrices;
